<template>
  <ion-page>
    <ion-content class="owner-pin-verify-content">
      <StickyPageHeader title="Betreiber-PIN" @back="dismiss" />

      <div class="owner-pin-verify-page">
        <p class="owner-pin-verify-intro">
          Nur für Inhaber – sensible Bereiche freischalten.
        </p>
        <p v-if="errorMessage" class="owner-pin-verify-error">{{ errorMessage }}</p>

        <PinNativeInput
          ref="pinInput"
          v-model="pin"
          aria-label="Betreiber-PIN"
          @update:model-value="onPinChange"
        />

        <button
          type="button"
          class="primary-action-btn"
          :disabled="pin.length !== 4 || verifying"
          @click="verify"
        >
          {{ verifying ? 'Wird geprüft…' : 'Freischalten' }}
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue';
import { IonPage, IonContent, modalController } from '@ionic/vue';
import { api } from '@/utils/apiService';
import PinNativeInput from '@/components/ui/PinNativeInput.vue';
import StickyPageHeader from '@/components/ui/StickyPageHeader.vue';

const pin = ref('');
const errorMessage = ref('');
const verifying = ref(false);
const pinInput = ref(null);

const dismiss = () => modalController.dismiss(null, 'cancel');

const verify = async () => {
  if (pin.value.length !== 4 || verifying.value) return;
  verifying.value = true;
  errorMessage.value = '';
  try {
    const res = await api.post('/company/owner-pin/verify', { pin: pin.value });
    if (res.status === 200 && res.data?.verifiedUntil) {
      await modalController.dismiss({ verifiedUntil: res.data.verifiedUntil }, 'verified');
      return;
    }
    if (res.status === 429 && res.data?.error === 'pin_rate_limited') {
      const minutes = res.data.retryAfterSeconds
        ? Math.max(1, Math.ceil(res.data.retryAfterSeconds / 60))
        : null;
      errorMessage.value = minutes
        ? `Zu viele Fehlversuche. Bitte in ${minutes} Min. erneut versuchen.`
        : 'Zu viele Fehlversuche. Bitte später erneut versuchen.';
      pin.value = '';
      return;
    }
    if (res.status === 403 && res.data?.error === 'pin_not_configured') {
      await modalController.dismiss({ notRequired: true }, 'not_required');
      return;
    }
    if (res.status === 403) {
      if (res.data?.warnLockout && res.data?.attemptsUntilLockout > 0) {
        const suffix = res.data.attemptsUntilLockout === 1 ? 'Versuch' : 'Versuche';
        errorMessage.value = `PIN ist falsch. Noch ${res.data.attemptsUntilLockout} ${suffix}, dann wird die Eingabe gesperrt.`;
      } else {
        errorMessage.value = res.data?.error === 'pin_not_configured'
          ? 'Betreiber-PIN ist noch nicht aktiv.'
          : 'PIN ist falsch.';
      }
      pin.value = '';
      return;
    }
    errorMessage.value = 'Prüfung fehlgeschlagen. Bitte erneut versuchen.';
  } catch (e) {
    console.error('owner pin verify', e);
    errorMessage.value = 'Prüfung fehlgeschlagen. Bitte erneut versuchen.';
  } finally {
    verifying.value = false;
    await nextTick();
    pinInput.value?.focus();
  }
};

const onPinChange = (value) => {
  if (value.length < 4) {
    errorMessage.value = '';
  } else if (value.length === 4 && !verifying.value) {
    void verify();
  }
};

onMounted(async () => {
  await nextTick();
  pinInput.value?.focus();
});
</script>

<style scoped lang="scss">
@import '@/styles/profile-tab.scss';

.owner-pin-verify-content {
  --background: #fff;
}

.owner-pin-verify-page {
  padding: 8px 20px 40px;
}

.owner-pin-verify-intro {
  font-size: 15px;
  line-height: 1.5;
  color: #333;
  margin: 0 0 24px;
}

.owner-pin-verify-error {
  margin: 0 0 16px;
  color: #c62828;
  font-size: 14px;
}

.primary-action-btn {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  background: $profile-fab-blue;
  color: #fff;
  font-size: 16px;
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
  }
}

:deep(.pin-display-row) {
  justify-content: center;
}
</style>
